import { Agent } from "app-types/agent";
import { AgentContextService } from "./agent-context-service";

/**
 * Service for handling cleanup when switching between agent contexts
 */
export class AgentCleanupService {
  private static currentAgent: Agent | null = null;
  private static cleanupCallbacks: Array<() => void> = [];

  /**
   * Register a cleanup callback
   */
  static registerCleanupCallback(callback: () => void): () => void {
    this.cleanupCallbacks.push(callback);

    // Return unregister function
    return () => {
      const index = this.cleanupCallbacks.indexOf(callback);
      if (index > -1) {
        this.cleanupCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Set the current agent context
   */
  static setCurrentAgent(agent: Agent | null): void {
    const previousAgent = this.currentAgent;
    this.currentAgent = agent;

    // If switching agents, perform cleanup
    if (previousAgent && previousAgent.id !== agent?.id) {
      this.performCleanup(previousAgent);
    }
  }

  /**
   * Get the current agent
   */
  static getCurrentAgent(): Agent | null {
    return this.currentAgent;
  }

  /**
   * Perform cleanup for the previous agent context
   */
  private static performCleanup(previousAgent: Agent): void {
    try {
      // Run all registered cleanup callbacks
      this.cleanupCallbacks.forEach((callback) => {
        try {
          callback();
        } catch (error) {
          console.warn("Error in cleanup callback:", error);
        }
      });

      // Clear any temporary data for the previous agent
      this.clearTemporaryData(previousAgent);

      // Update context service
      AgentContextService.cleanupContext(previousAgent);
    } catch (error) {
      console.warn("Error during agent cleanup:", error);
    }
  }

  /**
   * Clear temporary data for an agent
   */
  private static clearTemporaryData(agent: Agent): void {
    try {
      // Clear any cached data specific to this agent
      const cacheKeys = [
        `agent-${agent.id}-cache`,
        `agent-${agent.id}-temp`,
        `agent-${agent.id}-state`,
      ];

      cacheKeys.forEach((key) => {
        try {
          sessionStorage.removeItem(key);
        } catch (_error) {
          // Ignore storage errors
        }
      });
    } catch (error) {
      console.warn("Error clearing temporary data:", error);
    }
  }

  /**
   * Force cleanup of all agent contexts
   */
  static forceCleanupAll(): void {
    try {
      // Run all cleanup callbacks
      this.cleanupCallbacks.forEach((callback) => {
        try {
          callback();
        } catch (error) {
          console.warn("Error in cleanup callback:", error);
        }
      });

      // Clear all agent-related storage
      AgentContextService.clearAgentContext();
      AgentContextService.clearAllAgentThreadMappings();

      // Clear current agent
      this.currentAgent = null;

      // Clear session storage
      this.clearAllTemporaryData();
    } catch (error) {
      console.warn("Error during force cleanup:", error);
    }
  }

  /**
   * Clear all temporary data
   */
  private static clearAllTemporaryData(): void {
    try {
      // Get all session storage keys
      const keys = Object.keys(sessionStorage);

      // Remove agent-related keys
      keys.forEach((key) => {
        if (
          key.startsWith("agent-") &&
          (key.includes("-cache") ||
            key.includes("-temp") ||
            key.includes("-state"))
        ) {
          try {
            sessionStorage.removeItem(key);
          } catch (_error) {
            // Ignore storage errors
          }
        }
      });
    } catch (error) {
      console.warn("Error clearing all temporary data:", error);
    }
  }

  /**
   * Handle page unload cleanup
   */
  static handlePageUnload(): void {
    try {
      // Save current state before unload
      if (this.currentAgent) {
        AgentContextService.saveAgentContext(this.currentAgent);
      }

      // Clear temporary data
      this.clearAllTemporaryData();
    } catch (error) {
      console.warn("Error during page unload cleanup:", error);
    }
  }

  /**
   * Initialize cleanup service
   */
  static initialize(): void {
    // Handle page unload
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", this.handlePageUnload.bind(this));

      // Handle visibility change (tab switching)
      document.addEventListener("visibilitychange", () => {
        if (document.hidden && this.currentAgent) {
          // Save context when tab becomes hidden
          AgentContextService.saveAgentContext(this.currentAgent);
        }
      });
    }
  }
}
